import { useState } from "react";
import { useNavigate } from "@tanstack/react-router";
import { Loader2, MessageSquare, RotateCw, Sparkle, UploadCloud } from "lucide-react";
import { useTranslation } from "react-i18next";
import { usePlaygroundParametersStore } from "@/atoms/playground";
import RegisterModelForRagDialog from "@/components/RegisterModelForRagDialog";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { useCatalogSyncStore, type CatalogStatus as Status } from "../catalog-sync";
import type { OmnimizerDeployment } from "../types";

export function useOpenInPlayground() {
  const navigate = useNavigate();
  const setLlmConfigId = usePlaygroundParametersStore((s) => s.setLlmConfigId);
  return (llmConfigId: string) => {
    setLlmConfigId(llmConfigId);
    void navigate({ to: "/playground/{-$id}", params: {} });
  };
}

export function CatalogStatus({
  deployment,
  status,
  compact = false,
}: {
  deployment: OmnimizerDeployment;
  status: Status | undefined;
  compact?: boolean;
}) {
  const { t } = useTranslation();
  const openInPlayground = useOpenInPlayground();
  const retry = useCatalogSyncStore((s) => s.finish);
  const [registering, setRegistering] = useState(false);
  if (!status || status.state === "unsupported") return null;

  if (status.state === "published")
    return (
      <div className="flex flex-wrap items-center gap-2">
        <Badge variant="success" className="gap-1">
          <Sparkle className="size-3" aria-hidden />
          {t("omnimizer.catalog.published")}
        </Badge>
        <Button
          size="sm"
          variant={compact ? "ghost" : "outline"}
          onClick={() => openInPlayground(status.entry.id)}
          aria-label={t("omnimizer.catalog.tryInPlaygroundFor", { name: deployment.name })}
        >
          <MessageSquare aria-hidden />
          {t("omnimizer.catalog.tryInPlayground")}
        </Button>
      </div>
    );

  if (status.state === "publishing")
    return (
      <Badge variant="muted" className="gap-1" role="status">
        <Loader2 className="size-3 animate-spin motion-reduce:animate-none" aria-hidden />
        {t("omnimizer.catalog.publishing")}
      </Badge>
    );

  if (status.state === "waiting")
    return <Badge variant="muted">{t("omnimizer.catalog.waiting")}</Badge>;

  if (status.state === "notOwner")
    return <Badge variant="outline">{t("omnimizer.catalog.notOwner")}</Badge>;

  if (status.state === "failed")
    return (
      <div className="flex flex-wrap items-center gap-2">
        <Badge variant="destructive" title={status.error}>
          {t("omnimizer.catalog.failed")}
        </Badge>
        <Button size="sm" variant="ghost" onClick={() => retry(deployment.model_id)}>
          <RotateCw aria-hidden />
          {t("omnimizer.catalog.retry")}
        </Button>
      </div>
    );

  return (
    <div className="flex flex-wrap items-center gap-2">
      <Badge variant="muted">{t("omnimizer.catalog.needsProject")}</Badge>
      <Button size="sm" variant="outline" onClick={() => setRegistering(true)}>
        <UploadCloud aria-hidden />
        {t("omnimizer.catalog.publish")}
      </Button>
      {registering && (
        <RegisterModelForRagDialog
          open
          onClose={() => setRegistering(false)}
          deployment={deployment}
          availableApiKeys={undefined}
          endpointUrl={deployment.endpoint_url}
        />
      )}
    </div>
  );
}
